import { AgentSearchPage } from './agent/AgentSearchPage'
import { CartPage } from './cart/CartPage'
import { CatalogBrowsePage } from './catalog/CatalogBrowsePage'
import { CatalogSearchPage } from './catalog/CatalogSearchPage'
import { CatalogSeriesConfigurePage } from './catalog/CatalogSeriesConfigurePage'
import { CatalogSeriesPage } from './catalog/CatalogSeriesPage'
import { CheckoutPage } from './checkout/CheckoutPage'
import { OrderDetailPage } from './orders/OrderDetailPage'
import { OrdersListPage } from './orders/OrdersListPage'
import { BomDetailPage } from './parts/BomDetailPage'
import { PartDetailPage } from './parts/PartDetailPage'
import { PartsLibraryPage } from './parts/PartsLibraryPage'
import { RfqDetailPage } from './rfq/RfqDetailPage'
import { RfqListPage } from './rfq/RfqListPage'
import { RfqReviewPage } from './rfq/RfqReviewPage'
import type { VerticalModule } from './types'
import { StagingPage } from './uploads/StagingPage'
import { UploadsPage } from './uploads/UploadsPage'
import { AllWorkspacesPage } from './workspaces/AllWorkspacesPage'
import { WorkspacePage } from './workspaces/WorkspacePage'

/**
 * The vertical registry (UID-9). One entry per IMTS demo vertical (WS-2..5,
 * IMTS§4). Each owns a DISJOINT base path and contributes its screens + nav
 * entries; `composeVerticals` (./compose.ts) merges them and rejects any
 * collision at load time. To add a real screen, a vertical owner edits ONLY
 * its own entry here — never the shell, never another vertical's entry (the
 * SPA mirror of UID-7's per-vertical OpenAPI fragment ownership).
 *
 * Skeleton note: each vertical ships ONE placeholder route to prove the seam;
 * real screens land in WS-2..5. The `requiredCapability` on the nav items is
 * ILLUSTRATIVE of the role-aware-nav seam (it demonstrates Engineer vs
 * Purchaser differ, UID-9 deliverable 5) — the real per-item gating is each
 * vertical's own call. Capabilities come from `@misumi/shared` (`ROLE_CAPABILITIES`).
 */
export const VERTICAL_MODULES: readonly VerticalModule[] = [
  {
    // WS-2 — Catalog & Quick-Buy (Act 1). Baseline: every authenticated role.
    // UID-253 rebuild (ADR-0091): `/catalog`'s default landing is the design
    // source of truth's `browse` stage's "Shop by category" section, not the
    // flat "All products" ResultCard grid an earlier pass built here — see
    // `CatalogBrowsePage.tsx`'s doc comment for the full scope decision.
    id: 'catalog',
    basePath: '/catalog',
    // UID-246 (2026-08-23 design refresh, README §5): 'Catalog' → 'Search catalog'.
    navItems: [{ label: 'Search catalog', to: '/catalog' }],
    routes: [
      {
        path: '/catalog',
        element: <CatalogBrowsePage />,
      },
      // UID-251: the keyword-search results screen, reached from the top
      // bar's search field (TopBar.tsx) submitting a query — see
      // CatalogSearchPage.tsx's doc comment.
      {
        path: '/catalog/search',
        element: <CatalogSearchPage />,
      },
      // UID-286: the series-card grid for one leaf category — reached by
      // clicking a leaf-category chip in "Shop by category" (see
      // CatalogBrowsePage.tsx's handleCategoryClick).
      {
        path: '/catalog/series/:category',
        element: <CatalogSeriesPage />,
      },
      // UID-34 (ADR-0093 Surface A): the per-series configurator page,
      // reached by a SeriesCard's "View types" click (CatalogSeriesPage's
      // handleOpen).
      {
        path: '/catalog/series/:category/:seriesCode',
        element: <CatalogSeriesConfigurePage />,
      },
    ],
  },
  {
    // UID-36 — the sourcing agent (README §7 screen 2 / §13 Epic C1):
    // conversational search over "MISUMI catalog + your Fictiv library."
    // No primary nav entry (like `workspaces` below) — this screen is
    // reached from the catalog search no-match state's "Ask the sourcing
    // agent" action (`CatalogSearchPage.tsx`) and, per the design source,
    // the Home command-center's own AI prompt bar (not yet built) — never
    // from the left rail directly.
    id: 'agent',
    basePath: '/agent',
    routes: [
      {
        path: '/agent',
        element: <AgentSearchPage />,
      },
    ],
  },
  {
    // WS-4 — RFQ → Quote → CPQ → Handoff (Acts 2b, 3). Engineer submits RFQs.
    // UID-55: the real Quotes/RFQs list (screens.md §14). UID-56 adds the
    // build→review→submit screen at `/rfq/new` (screens.md §15).
    //
    // Nav gate is `account:read` (buyer baseline), NOT `rfq:submit`: the
    // backend contract (apps/api/openapi/paths/rfq.yaml) deliberately gates
    // `GET /rfq` at `account:read` because the Purchaser — who holds no
    // `rfq:submit` — is this screen's main actor in Act 4 (accepting a
    // returned quote into cart). The `/rfq/new` ROUTE is reachable by any
    // authenticated role (`RequireAuth` only), but `RfqReviewPage` itself
    // gates on `rfq:submit` and renders a permission dead-end for anyone
    // without it (Engineer-only) — mirroring how `POST /rfq` gates
    // server-side, and the same read/write split ADR-0036 §4 already
    // established for Orders (`order:read` baseline vs. narrow
    // `order:submit`). The "+ New RFQ request" entry point on
    // `RfqListPage` is itself only shown to a role that holds `rfq:submit`,
    // so a Purchaser never sees a link into a screen it can't use.
    //
    // UID-289 (ADR-0094 D2, 2026-08-25 design refresh): reordered ahead of
    // the WS-3 Uploads/Part library entries (array order = nav render
    // order, Sidebar.tsx) and relabeled 'Quotes' → 'RFQ' (UID-246 had
    // already shortened it from 'Quotes & RFQs'; this ticket's ref shows a
    // bare 'RFQ'). Nav gating/capability is unchanged.
    id: 'rfq',
    basePath: '/rfq',
    // UID-246 (2026-08-23 design refresh, README §5): 'Quotes & RFQs' → 'Quotes'.
    // UID-301 (design-fidelity pass): the authoritative prototype
    // (`Fictiv Platform.dc.html:235`) labels this nav item 'RFQ', not 'Quotes'
    // — reversing UID-246's rename. The Sidebar (not this array's order) now
    // owns the two-group ordering the prototype draws.
    navItems: [
      {
        label: 'RFQ',
        to: '/rfq',
        requiredCapability: 'account:read',
      },
    ],
    routes: [
      {
        path: '/rfq',
        element: <RfqListPage />,
      },
      {
        path: '/rfq/new',
        element: <RfqReviewPage />,
      },
      // UID-135 (ADR-0056): the real list->detail route, modelled on
      // `/orders/:orderId` — see RfqDetailPage.tsx's doc comment.
      {
        path: '/rfq/:rfqId',
        element: <RfqDetailPage />,
      },
    ],
  },
  {
    // orders. Multi-basePath (UID-70): owns '/orders' (UID-63 — the real
    // Orders list + detail screens, docs/design/uid-63-orders-ux-spec.md),
    // '/cart' (UID-59 — the real Cart screen), and now '/checkout' (UID-60 —
    // the real Checkout screen, docs/design/uid-60-checkout-ux-spec.md). The
    // Cart is reached from the global-chrome cart icon in the top bar
    // (screens.md §1), NOT the left rail, and Checkout is reached only from
    // Cart's "Proceed to checkout" button — neither gets its own left-nav
    // entry, so this vertical contributes only the 'Orders' nav item.
    //
    // The Orders nav/route is gated `order:read` — the whole account can view
    // its order history (product call, ADR "orders-list-read-surface"): that
    // capability is in the buyer baseline, so every role sees the Orders nav.
    // Placing an order requires `order:submit` — Purchaser-only in production,
    // plus a DEMO-ONLY grant to `engineer` (`packages/shared/src/authz.ts`, not
    // a production RBAC decision) — enforced INSIDE `<CheckoutPage>` itself
    // (matching `<ManageUsersPage>`'s pattern:
    // a non-Purchaser who lands on `/checkout` — e.g. via direct URL — sees a
    // graceful "not available for your role" panel with a link back to the
    // cart, rather than a route-level redirect/404; the BFF independently
    // enforces `order:submit` on `POST /api/orders` regardless, ADR-0038 §2).
    //
    // UID-289 (ADR-0094 D2): reordered ahead of Uploads/Part library, per
    // the design refresh's primary-group ordering (Home → Search catalog →
    // RFQ → Orders, then a hairline divider before the tools group).
    id: 'orders',
    basePath: ['/orders', '/cart', '/checkout'],
    navItems: [{ label: 'Orders', to: '/orders', requiredCapability: 'order:read' }],
    routes: [
      {
        path: '/orders',
        element: <OrdersListPage />,
      },
      {
        path: '/orders/:orderId',
        element: <OrderDetailPage />,
      },
      { path: '/cart', element: <CartPage /> },
      { path: '/checkout', element: <CheckoutPage /> },
    ],
  },
  {
    // WS-3 — Intake → Library → Config (Act 2a). Engineer-facing (BOM upload).
    // Two disjoint basePaths (UID-70 pattern): '/parts' (library/detail/BOM)
    // and '/uploads' (CAD intake) — one vertical, two prefixes it owns.
    //
    // UID-289 (ADR-0094 D2): moved BELOW RFQ/Orders (the "tools" group,
    // separated by a hairline divider — Sidebar.tsx) — was previously
    // directly after Search catalog.
    id: 'parts',
    basePath: ['/parts', '/uploads'],
    // UID-246 (2026-08-23 design refresh, README §5): Uploads renders BEFORE
    // Part library (array order = nav order, Sidebar.tsx); 'Parts library' →
    // 'Part library' (singular).
    navItems: [
      { label: 'Uploads', to: '/uploads', requiredCapability: 'bom:upload' },
      {
        label: 'Part library',
        to: '/parts',
        requiredCapability: 'bom:upload',
      },
    ],
    routes: [
      {
        path: '/parts',
        element: <PartsLibraryPage />,
      },
      {
        path: '/parts/:partId',
        element: <PartDetailPage />,
      },
      {
        path: '/parts/boms/:bomId',
        element: <BomDetailPage />,
      },
      {
        path: '/uploads',
        element: <UploadsPage />,
      },
      {
        path: '/uploads/:sessionId',
        element: <StagingPage />,
      },
    ],
  },
  {
    // WS-3 — Workspaces (UID-48b, screens.md §8/§9). Team-collaboration
    // container spanning BOMs/RFQs/orders — every authenticated role sees
    // it (baseline, like Catalog); no `Capability` gates it specifically.
    id: 'workspaces',
    // UID-345: the URL now reads "/projects" (the module id, `:workspaceId`
    // param name, and backend `/workspaces` endpoints are unchanged — this is
    // a route-path-only rename, matching UID-174's earlier label-only rebrand).
    basePath: '/projects',
    // UID-246 (2026-08-23 design refresh, README §5): NO primary nav entry —
    // "Projects" duplicated the below-divider `sidebar-workspaces` section
    // Sidebar.tsx already renders (its own header + empty state). Routes stay;
    // only the redundant top-level link is removed (navItems is optional).
    routes: [
      {
        path: '/projects',
        element: <AllWorkspacesPage />,
      },
      {
        path: '/projects/:workspaceId',
        element: <WorkspacePage />,
      },
    ],
  },
]
